import React, { useEffect, useRef, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Camera, Upload, Trash2, FileText, Receipt, Loader2, BarChart3 } from "lucide-react";
import { toast, Toaster } from "sonner";

const CATEGORIES = ["travel", "meals", "samples", "printing", "lodging", "communication", "other"];
const STATUS_BADGE = {
    pending: "bg-amber-50 text-amber-800",
    approved: "bg-sky-50 text-sky-800",
    rejected: "bg-red-50 text-red-800",
    paid: "bg-emerald-50 text-emerald-800",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const blank = { expense_date: todayISO(), category: "travel", amount: "", description: "" };

export default function MRMarketExpenses() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [form, setForm] = useState(blank);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);

    const load = async () => {
        try {
            const [list, summ] = await Promise.all([
                api.get("/mr/expenses"),
                api.get("/mr/expenses/summary"),
            ]);
            setItems(list.data);
            setSummary(summ.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be greater than 0");
        if (!form.expense_date) return toast.error("Pick a date");
        setBusy(true);
        const fd = new FormData();
        fd.append("expense_date", form.expense_date);
        fd.append("category", form.category);
        fd.append("amount", form.amount);
        fd.append("description", form.description || "");
        if (file) fd.append("file", file);
        try {
            await api.post("/mr/expenses", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            toast.success("Expense submitted");
            setForm(blank); setFile(null); load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Submission failed");
        } finally { setBusy(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this expense? Only pending entries can be deleted.")) return;
        try { await api.delete(`/mr/expenses/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Cannot delete approved/paid expenses"); }
    };

    const totalPending = items.filter(i => i.status === "pending").reduce((s, i) => s + (i.amount || 0), 0);
    const totalPaid = items.filter(i => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);
    const apiBase = (api.defaults.baseURL || "").replace(/\/api$/, "");

    return (
        <div data-testid="mr-expenses">
            <Toaster position="top-center" />
            <div className="mb-6">
                <div className="label-eyebrow text-sky-700">MR · field expenses</div>
                <h1 className="font-serif text-4xl text-slate-900 mt-2">Market expenses</h1>
                <p className="text-sm text-stone-600 mt-2 max-w-xl">
                    Snap a receipt with your camera or pick from gallery, log the amount &amp; reason, and submit. Accounts will review and mark paid.
                </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-6">
                <Stat label="Pending" value={formatINR(totalPending)} colour="amber" />
                <Stat label="Paid (total)" value={formatINR(totalPaid)} colour="emerald" />
                <Stat label="Entries" value={items.length} colour="sky" />
            </div>

            {/* Period summary */}
            {summary && (
                <div className="bg-white border border-sky-100 p-5 mb-8" data-testid="expense-summary">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h2 className="font-serif text-2xl flex items-center gap-2 text-slate-900">
                            <BarChart3 className="w-5 h-5 text-sky-700" />Spend overview
                        </h2>
                        <div className="text-xs text-stone-500">All time: <span className="font-mono text-stone-800">{formatINR(summary.totals.all_time)}</span></div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 mb-5">
                        <Stat label="Today" value={formatINR(summary.totals.today)} colour="sky" />
                        <Stat label="This month" value={formatINR(summary.totals.this_month)} colour="sky" />
                        <Stat label="This year" value={formatINR(summary.totals.this_year)} colour="sky" />
                    </div>
                    <Tabs defaultValue="daily">
                        <TabsList className="rounded-none bg-sky-50 border border-sky-100 p-0 h-auto">
                            <TabsTrigger value="daily" className="rounded-none px-4 py-2 text-xs data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="tab-daily">Day-wise (last 30)</TabsTrigger>
                            <TabsTrigger value="monthly" className="rounded-none px-4 py-2 text-xs data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="tab-monthly">Month-wise (last 12)</TabsTrigger>
                            <TabsTrigger value="yearly" className="rounded-none px-4 py-2 text-xs data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="tab-yearly">Year-wise</TabsTrigger>
                        </TabsList>
                        <TabsContent value="daily" className="mt-3"><PeriodTable rows={summary.daily} periodLabel="Day" /></TabsContent>
                        <TabsContent value="monthly" className="mt-3"><PeriodTable rows={summary.monthly} periodLabel="Month" /></TabsContent>
                        <TabsContent value="yearly" className="mt-3"><PeriodTable rows={summary.yearly} periodLabel="Year" /></TabsContent>
                    </Tabs>
                </div>
            )}

            <div className="bg-white border border-sky-100 p-5 mb-8 space-y-4" data-testid="expense-form">
                <h2 className="font-serif text-2xl">New expense</h2>
                <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Date *</Label>
                        <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="exp-date" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Category *</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="exp-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Amount (₹) *</Label>
                        <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="exp-amount" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Description</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-none border-stone-300 mt-1" placeholder="e.g. Cab to Dr. Mehta clinic, Andheri" data-testid="exp-desc" />
                </div>

                {/* Camera / gallery */}
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Receipt photo</Label>
                    <div className="flex gap-3 flex-wrap items-start">
                        <input
                            ref={cameraRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }}
                            data-testid="exp-camera-input"
                        />
                        <input
                            ref={galleryRef}
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }}
                            data-testid="exp-gallery-input"
                        />
                        <Button type="button" variant="outline" onClick={() => cameraRef.current?.click()} className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50" data-testid="exp-camera">
                            <Camera className="w-4 h-4 mr-2" />Take photo
                        </Button>
                        <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} className="rounded-none border-stone-400" data-testid="exp-gallery">
                            <Upload className="w-4 h-4 mr-2" />From gallery / file
                        </Button>
                        {file && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-sky-50 border border-sky-100 text-sm">
                                {file.type?.startsWith("image/") ? (
                                    <img src={URL.createObjectURL(file)} alt="" className="w-10 h-10 object-cover" />
                                ) : <FileText className="w-5 h-5" />}
                                <div>
                                    <div className="text-xs">{file.name}</div>
                                    <div className="text-[10px] text-stone-500">{Math.round(file.size / 1024)} KB</div>
                                </div>
                                <button type="button" onClick={() => setFile(null)} className="text-red-700 ml-2" aria-label="Remove receipt"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>
                    <p className="text-[10px] text-stone-500 mt-2">"Take photo" opens your phone's camera directly. JPG/PNG/WEBP/PDF up to 8 MB.</p>
                </div>

                <Button onClick={submit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="exp-submit">
                    {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : <><Receipt className="w-4 h-4 mr-2" />Submit expense</>}
                </Button>
            </div>

            <h2 className="font-serif text-2xl text-slate-900 mb-3">Your submitted expenses</h2>
            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-sky-50 border-b border-sky-100">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Date</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Category</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Amount</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Description</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Receipt</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={7} className="p-10 text-center text-stone-500">No expenses yet. Add your first one above.</td></tr>
                        ) : items.map((e) => (
                            <tr key={e.id} className="border-b border-stone-100" data-testid={`exp-row-${e.id}`}>
                                <td className="p-3 text-stone-700 whitespace-nowrap">{e.expense_date}</td>
                                <td className="p-3 text-xs capitalize">{e.category}</td>
                                <td className="p-3 text-right font-mono">{formatINR(e.amount)}</td>
                                <td className="p-3 text-stone-600 max-w-xs truncate">{e.description || "—"}</td>
                                <td className="p-3">
                                    {e.receipt_url ? (
                                        <a href={`${apiBase}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-sky-700 underline text-xs">View</a>
                                    ) : <span className="text-stone-400 text-xs">none</span>}
                                </td>
                                <td className="p-3">
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${STATUS_BADGE[e.status] || "bg-stone-100 text-stone-700"}`}>{e.status}</span>
                                    {e.notes && <div className="text-[10px] text-stone-500 mt-1">{e.notes}</div>}
                                </td>
                                <td className="p-3 text-right">
                                    {e.status === "pending" && (
                                        <button onClick={() => del(e.id)} className="text-red-700" data-testid={`exp-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const Stat = ({ label, value, colour }) => {
    const colorMap = {
        amber: "border-amber-200 bg-amber-50 text-amber-900",
        emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
        sky: "border-sky-200 bg-sky-50 text-sky-900",
    };
    return (
        <div className={`p-4 border ${colorMap[colour] || ""}`}>
            <div className="label-eyebrow opacity-70">{label}</div>
            <div className="font-serif text-2xl mt-1">{value}</div>
        </div>
    );
};

const PeriodTable = ({ rows, periodLabel }) => {
    if (!rows || rows.length === 0) return <p className="text-sm text-stone-500 py-4">No expenses logged yet.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead><tr className="bg-sky-50 border-b border-sky-100">
                    <th className="text-left p-2 text-xs uppercase tracking-widest text-stone-500">{periodLabel}</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Total</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Paid</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Pending</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Entries</th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.period} className="border-b border-stone-100" data-testid={`period-row-${r.period}`}>
                            <td className="p-2 font-mono text-stone-800">{r.period}</td>
                            <td className="p-2 text-right font-mono">{`₹${r.total.toFixed(2)}`}</td>
                            <td className="p-2 text-right font-mono text-emerald-700">{`₹${r.paid.toFixed(2)}`}</td>
                            <td className="p-2 text-right font-mono text-amber-700">{`₹${r.pending.toFixed(2)}`}</td>
                            <td className="p-2 text-right text-stone-500">{r.count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
